/* One rule for the things a view asks the box for.
 *
 * The plan, a history window and a price day are all asked for rather than
 * streamed, and all three had the same fault: a carrier that drops settles
 * every request in flight as a failure at once, and then nothing asked again.
 * The phase goes back to 'streaming' seconds later with the box answering
 * happily, and the screen keeps a sentence promising something it will never
 * do — with no reconnect button anywhere in this app to rescue it, because
 * healing is supposed to be this file's job.
 *
 * The other half of that job is here for the same reason. Most failed asks
 * never move the session phase at all — a bulk answer lost on the relay, the
 * eight-second deadline against a busy box, E_BOOTING for the minutes after
 * an update — so a reconnect is not coming to rescue those, and a screen that
 * waits for one waits until the tab is closed. The price chart was given a
 * retry of its own and the plan and the history were not, which is how the
 * same view ended up healing one of its two halves.
 *
 * Held in one place because it was three, and three near-copies of a rule
 * this small drift into three slightly different rules.
 */

import { onDestroy } from 'svelte'
import type { SiteStore } from './site.svelte'

/**
 * How long to wait before asking again after an ask that failed, and the
 * longest that wait is allowed to grow to.
 *
 * The wait doubles so a struggling box is asked less often, not more, and
 * stops growing at a quarter of an hour: four asks an hour is not a poll, and
 * a box that comes back at noon should not have to wait for the next thing
 * that happens to change to be believed.
 */
const RETRY_MS = 30_000
const RETRY_CEILING_MS = 15 * 60_000

/**
 * Ask while the session is live, again every time it comes back, and again
 * after an ask that failed.
 *
 * `what` names the thing being asked for. The same name is a question already
 * answered and is not asked twice; a new name is a new question. `null` means
 * there is nothing to ask this box for at all — a box with no price feed, say
 * — and is not a failure.
 *
 * `ask` says whether the box answered by settling: a rejected promise is an
 * ask to make again later. Nothing else here can tell — a failure the caller
 * swallows looks exactly like an answer.
 *
 * Nothing is asked while the session is not streaming: a request handed to a
 * carrier that is down is dropped on the floor and waits out its own deadline
 * before the view hears about it.
 *
 * Must be called during component setup, like any effect. The ask runs inside
 * an effect, so it must not write anything `what` reads, or it would loop.
 */
export function askWhenLive(
  site: SiteStore,
  what: () => string | null,
  ask: () => Promise<unknown>
): void {
  let asked: string | null = null
  let waitMs = RETRY_MS
  let timer: ReturnType<typeof setTimeout> | undefined

  /** Which ask is the current one. A superseded one's outcome is not news. */
  let gen = 0

  /** Bumped by the wait, which is what turns a failure back into an ask. */
  let tries = $state(0)

  onDestroy(() => {
    // An ask in flight settles after the component is gone, and a failure
    // then schedules a retry no unmount is left to clear. Superseding it
    // here makes the late settle find itself stale, the same way any newer
    // ask does.
    gen += 1
    clearTimeout(timer)
  })

  $effect(() => {
    const want = what()
    const attempt = tries

    if (site.session.phase !== 'streaming' || want === null) {
      // Whatever was asked before is now unanswered — either it failed with
      // the carrier or it never went out — so the next live moment asks again
      // rather than waiting out a backoff. A reconnect is a fresh start and
      // not the continuation of a run of failures: whatever the box was busy
      // with, it has restarted since.
      asked = null
      waitMs = RETRY_MS
      clearTimeout(timer)
      return
    }

    // The attempt is part of the question, so asking again after a failure is
    // the same rule as asking again for a new day: a name that changed.
    const key = `${want} ${attempt}`
    if (asked === key) return
    asked = key

    // Any ask supersedes a wait about to make one. If a reconnect or the day
    // turning over asks first, the wait has nothing left to do, and letting it
    // fire spends a bulk round trip on a question already answered.
    clearTimeout(timer)

    const mine = ++gen
    void ask().then(
      () => {
        if (mine === gen) waitMs = RETRY_MS
      },
      () => {
        if (mine !== gen) return
        timer = setTimeout(() => (tries += 1), waitMs)
        waitMs = Math.min(waitMs * 2, RETRY_CEILING_MS)
      }
    )
  })
}
